import { Link } from 'react-router-dom'
import type { QuotesSummaryPosition } from '../../../../shared/api/quotesSelection.api'
import { QuoteRow } from './QuoteRow'
import { COLUMN_KEYS, type ColumnKey } from '../hooks/useColumnWidths'

interface Props {
  position: QuotesSummaryPosition
  localSelectedQuoteId: string | null
  collapsed: boolean
  onToggleCollapsed: () => void
  /** Выбрать квоту. Снять выбор нельзя — только сменить на другую квоту. */
  onSelectQuote: (quoteId: string) => void
  columnWidths: Record<ColumnKey, number>
  onStartResize: (key: ColumnKey) => (e: React.MouseEvent<HTMLDivElement>) => void
}

type CardStatus = 'ready' | 'waiting' | 'no-quotes'

function cardStatus(p: QuotesSummaryPosition, localSel: string | null): CardStatus {
  if (localSel != null) return 'ready'
  if (p.quotes.length === 0) return 'no-quotes'
  return 'waiting'
}

const STATUS_STYLES: Record<CardStatus, { border: string; badge: string; label: string }> = {
  ready: {
    border: 'border-l-green-500',
    badge: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    label: 'Готова к КП',
  },
  waiting: {
    border: 'border-l-amber-500',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    label: 'Ждёт выбора',
  },
  'no-quotes': {
    border: 'border-l-gray-300 dark:border-l-gray-600',
    badge: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    label: 'Нет квот',
  },
}

const COLUMN_HEADERS: Record<ColumnKey, { label: string; align?: 'left' | 'right' | 'center' }> = {
  radio: { label: '', align: 'center' },
  min: { label: '', align: 'left' },
  matrix: { label: 'Матрица', align: 'left' },
  supplier: { label: 'Поставщик', align: 'left' },
  procurer: { label: 'Закупщик', align: 'left' },
  date: { label: 'Дата', align: 'left' },
  price: { label: 'Цена', align: 'right' },
  note: { label: 'Заметка', align: 'left' },
}

const MATRIX_BY_SOURCE: Record<string, string> = {
  STOCK: 'Склад',
  CHINA: 'Китай',
  RF: 'РФ',
  MANUFACTURER: 'Производители',
  OTHER: 'Стоки',
}

function formatMobileDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yy = String(d.getFullYear() % 100).padStart(2, '0')
  return `${dd}.${mm}.${yy}`
}

export function PositionCard({
  position,
  localSelectedQuoteId,
  collapsed,
  onToggleCollapsed,
  onSelectQuote,
  columnWidths,
  onStartResize,
}: Props) {
  const status = cardStatus(position, localSelectedQuoteId)
  const styles = STATUS_STYLES[status]
  const selectedQuote = position.quotes.find((q) => q.id === localSelectedQuoteId)

  // Выбор можно только сменить на другую квоту, но не снять (решение Сергея
  // 14.09.2026): сохранение отправляет на сервер лишь выбранные квоты, и
  // «снятая» галочка после обновления страницы возвращалась. Поэтому клик по
  // уже выбранной квоте ничего не делает.
  const selectQuote = (quoteId: string) => {
    if (quoteId !== localSelectedQuoteId) onSelectQuote(quoteId)
  }

  return (
    <div
      className={`bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 border-l-4 ${styles.border}`}
    >
      <button
        type="button"
        onClick={onToggleCollapsed}
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <span className="text-xs text-gray-400 dark:text-gray-500 w-6 shrink-0">
          {position.index}
        </span>
        <Link
          to={`/request-items/${position.id}`}
          onClick={(e) => e.stopPropagation()}
          className="font-mono font-semibold text-sm text-gray-900 dark:text-gray-100 hover:text-brand-600 dark:hover:text-brand-400 truncate"
        >
          {position.partNumber}
        </Link>
        <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
          · {position.brand ?? '—'} · {position.quantity} шт
        </span>
        <span className="flex-1" />
        {selectedQuote && selectedQuote.priceCny != null && (
          <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
            выбрано: {selectedQuote.priceCny.toFixed(2)} ¥
          </span>
        )}
        {collapsed && (
          <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
            {position.quotes.length} квот
          </span>
        )}
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles.badge}`}>
          {styles.label}
        </span>
        <span className="text-gray-400 dark:text-gray-500 text-sm w-4">
          {collapsed ? '▸' : '▾'}
        </span>
      </button>

      {!collapsed && (
        <div className="px-2 pb-3">
          {position.quotes.length === 0 ? (
            <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">
              На эту позицию ещё нет ни одной квоты.
            </div>
          ) : (
            <>
              {/* Десктопная таблица */}
              <div className="overflow-x-auto hidden md:block">
                <table
                  className="text-sm"
                  style={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}
                >
                  <colgroup>
                    {COLUMN_KEYS.map((k) => (
                      <col key={k} style={{ width: columnWidths[k] }} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                      {COLUMN_KEYS.map((k) => {
                        const h = COLUMN_HEADERS[k]
                        const align =
                          h.align === 'right'
                            ? 'text-right'
                            : h.align === 'center'
                              ? 'text-center'
                              : 'text-left'
                        return (
                          <th
                            key={k}
                            className={`relative px-3 py-2 ${align} select-none`}
                            style={{ width: columnWidths[k] }}
                          >
                            <span className="truncate block">{h.label}</span>
                            <div
                              onMouseDown={onStartResize(k)}
                              className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-brand-300/60"
                              title="Перетащите для изменения ширины колонки"
                            />
                          </th>
                        )
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {position.quotes.map((q) => (
                      <QuoteRow
                        key={q.id}
                        quote={q}
                        isSelected={localSelectedQuoteId === q.id}
                        onSelect={() => selectQuote(q.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Мобильный список */}
              <div className="md:hidden space-y-2 px-2">
                {position.quotes.map((q) => {
                  const selected = localSelectedQuoteId === q.id
                  const selectable = q.price != null && q.price > 0
                  return (
                    <button
                      key={q.id}
                      type="button"
                      disabled={!selectable}
                      onClick={() => selectable && selectQuote(q.id)}
                      className={`w-full flex gap-2 p-2 rounded border text-left ${
                        !selectable ? 'opacity-60 cursor-not-allowed' : ''
                      } ${
                        selected
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-400 dark:border-green-700'
                          : 'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <input
                        type="radio"
                        checked={selected}
                        disabled={!selectable}
                        readOnly
                        className="mt-1 w-4 h-4 accent-green-600"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate">{q.supplierName}</span>
                          {q.isMin && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                              min
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {MATRIX_BY_SOURCE[q.sourceType] ?? q.sourceType} · {q.procurementUserName}{' '}
                          · <span className="font-mono">{formatMobileDate(q.createdAt)}</span>
                        </div>
                        <div className="text-sm font-medium mt-0.5">
                          {q.price == null ? (
                            <span className="text-xs italic text-gray-400">нет цены</span>
                          ) : (
                            <>
                              {q.price.toFixed(2)} {q.currency}
                              {q.currency !== 'CNY' && q.priceCny != null && (
                                <span className="ml-1.5 text-xs text-gray-500">
                                  ≈ {q.priceCny.toFixed(2)} ¥
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        {q.note && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 whitespace-pre-wrap break-words">
                            {q.note}
                          </div>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            </>
          )}

          <div className="px-4 pt-3 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
            <Link
              to={`/request-items/${position.id}`}
              className="text-brand-600 dark:text-brand-400 hover:underline"
            >
              + Добавить квоту
            </Link>
            <span>·</span>
            <Link
              to={`/request-items/${position.id}#history`}
              className="hover:text-gray-700 dark:hover:text-gray-200"
            >
              История изменений
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
